// Personal Data page — athlete-facing self-service info that doesn't
// belong on the Progress (body photos) page. Right now that's just the
// Sizes card, but this page is intentionally structured so we can grow
// it (emergency contact, dietary preferences, injury notes…) without
// restructuring routes later.
//
// Access mirrors the API:
//   - athlete self (viewing their own `/personal` route)
//   - head coach (admin) viewing any athlete via `/users/:userId/personal`
//   - assigned coach viewing their own athletes (read-only Sizes card)
//
// Non-matching coaches reach the page but the Sizes card hides itself,
// so they see a friendly empty state instead of a 403 error toast.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IdentificationCard } from "@phosphor-icons/react";
import BackButton from "@/components/app/BackButton";
import SizesSection from "@/components/app/SizesSection";

export default function PersonalData() {
  const { user } = useAuth();
  const params = useParams();
  const userId = params.userId || user?.id;
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        // /auth/me already covers "self"; coach/admin need the targeted
        // read so we can show the right name + assigned coach context.
        if (userId === user.id) {
          setProfile(user);
          return;
        }
        const { data } = await api.get(`/users/${userId}`);
        setProfile(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [userId, user]);

  if (!userId || !profile) return null;

  const isSelf = userId === user.id;
  const displayName = profile.name || profile.email || "Athlete";

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-8">
      <BackButton />

      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-moss text-white">
          <IdentificationCard size={22} weight="bold" />
        </div>
        <div>
          <div className="hs-label">Personal data</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">
            {isSelf ? "Your personal data" : `${displayName.split(" ")[0]}'s personal data`}
          </h1>
          <p className="text-ink-2 mt-2 max-w-xl">
            {isSelf
              ? "Keep your sizing current so your coaches can grab you gear or equipment that actually fits."
              : `Sizing and personal details for ${displayName}.`}
          </p>
        </div>
      </div>

      <SizesSection
        athleteId={userId}
        viewer={user}
        athleteCoachId={profile?.coach_id}
        athleteName={displayName}
      />
    </div>
  );
}
